'use client'

/**
 * Locale Context Provider
 * Provides runtime locale switching with cookie persistence
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { SupportedLocale, TranslationKeys, AllTranslationKeys } from './types'
import {
  LOCALE_DEFAULTS,
  LocaleCurrency,
  getLocaleCookieClient,
  setLocaleCookieClient,
  detectBrowserLocale
} from './locale-cookie'

// Import all translation files
import csTranslations from './cs.json'
import enTranslations from './en.json'
import deTranslations from './de.json'
import frTranslations from './fr.json'
import itTranslations from './it.json'
import nlTranslations from './nl.json'
import plTranslations from './pl.json'
import ptTranslations from './pt.json'
import esTranslations from './es.json'

const translations: Record<SupportedLocale, TranslationKeys> = {
  cs: csTranslations as unknown as TranslationKeys,
  en: enTranslations as unknown as TranslationKeys,
  de: deTranslations as unknown as TranslationKeys,
  fr: frTranslations as unknown as TranslationKeys,
  it: itTranslations as unknown as TranslationKeys,
  nl: nlTranslations as unknown as TranslationKeys,
  pl: plTranslations as unknown as TranslationKeys,
  pt: ptTranslations as unknown as TranslationKeys,
  es: esTranslations as unknown as TranslationKeys
}

interface LocaleContextType {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => Promise<void>
  t: (key: AllTranslationKeys, params?: Record<string, any>) => string
  currency: LocaleCurrency
  timezone: string
  isLoading: boolean
}

const LocaleContext = createContext<LocaleContextType | null>(null)

/**
 * Get translation by key with parameter interpolation
 */
function getTranslation(
  locale: SupportedLocale,
  key: string,
  params?: Record<string, any>
): string {
  const keys = key.split('.')
  let current: any = translations[locale]

  for (const k of keys) {
    if (current && typeof current === 'object' && k in current) {
      current = current[k]
    } else {
      console.warn(`Translation key not found: ${key}`)
      return key
    }
  }

  let result = typeof current === 'string' ? current : key

  // Parameter interpolation
  if (params && typeof result === 'string') {
    Object.keys(params).forEach(paramKey => {
      const placeholder = `{${paramKey}}`
      result = result.replace(new RegExp(placeholder, 'g'), String(params[paramKey]))
    })
  }

  return result
}

interface LocaleProviderProps {
  children: React.ReactNode
  initialLocale?: SupportedLocale
}

export function LocaleProvider({ children, initialLocale }: LocaleProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale || 'en')
  const [isLoading, setIsLoading] = useState(false)

  // Initialize locale from cookie or browser preference on mount (client-side only)
  useEffect(() => {
    if (initialLocale) {
      // If server provided initial locale, use it
      setLocaleState(initialLocale)
      return
    }

    // Check cookie first
    const cookieLocale = getLocaleCookieClient()
    if (cookieLocale) {
      setLocaleState(cookieLocale)
      return
    }

    // Fall back to browser detection
    const browserLocale = detectBrowserLocale()
    setLocaleState(browserLocale)
    setLocaleCookieClient(browserLocale)
  }, [initialLocale])

  const setLocale = useCallback(async (newLocale: SupportedLocale) => {
    setIsLoading(true)
    try {
      // Update state
      setLocaleState(newLocale)

      // Update cookie
      setLocaleCookieClient(newLocale)

      // Note: persisting to the user profile (preferred_language) is handled
      // by the calling component via PUT /api/account/update-language — the
      // auth context reverts the cookie to the DB value on load otherwise.
    } finally {
      setIsLoading(false)
    }
  }, [])

  const t = useCallback(
    (key: AllTranslationKeys, params?: Record<string, any>): string => {
      return getTranslation(locale, key, params)
    },
    [locale]
  )

  const value: LocaleContextType = {
    locale,
    setLocale,
    t,
    currency: LOCALE_DEFAULTS[locale].currency,
    timezone: LOCALE_DEFAULTS[locale].timezone,
    isLoading
  }

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  )
}

/**
 * Hook to access locale context
 */
export function useLocale(): LocaleContextType {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider')
  }
  return context
}

/**
 * Get currency for a specific locale
 */
export function getCurrencyForLocale(locale: SupportedLocale): LocaleCurrency {
  return LOCALE_DEFAULTS[locale].currency
}

/**
 * Get timezone for a specific locale
 */
export function getTimezoneForLocale(locale: SupportedLocale): string {
  return LOCALE_DEFAULTS[locale].timezone
}

// Export translations for server-side usage
export { translations as allTranslations }
